import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import {
  ArrowLeft, BarChart2, Calendar, Clock, FileText, Info, List, Maximize, Pause, Play, PlayCircle,
  Star, Volume2, VolumeX,
} from "react-feather";
import { Badge, Button, Callout, Card, EmptyState, Page, Select, TextArea } from "../../../ui";
import { QaFlag, QaReview, SAMPLE_REVIEW, Severity } from "./sampleReview";
import { Profile, getStudents, getVideoCallQaReview, teacherProfile } from "../../../Api/apiUtils";

import "./videoCallQaReview.scss";

/** A display name from a CMS teacher profile or student row (field names vary by API). */
const personName = (p: any): string | null => {
  if (!p || typeof p !== "object") return null;
  const n =
    p.fullName ||
    [p.firstName, p.lastName].filter(Boolean).join(" ").trim() ||
    p.teacherName ||
    p.studentName ||
    p.name;
  return typeof n === "string" && n.trim() ? n.trim() : null;
};

/** QA stores the teacher's CMS userId. users/get/user/profile/{userId} gives the user (and, for
 * a teacher, `id` = teacherId); teacher/profile?teacherId= has the name under teacherProfileDetails. */
/** add-class `studentid` is the student's CMS userId. Search students by userId, as the CMS
 * Student list's "Student User ID" filter does, and accept only a row whose userId matches:
 * a CMS that does not know the user (e.g. UAT viewing production classes) returns an
 * unfiltered list, and its first row must not be shown as this student. */
const studentNameByUserId = async (userId: string): Promise<string | null> => {
  const rows = await getStudents(0, 5, { userId }).catch(() => []);
  const row = Array.isArray(rows) ? rows.find((r: any) => String(r?.userId ?? "") === String(userId)) : null;
  return (row && String(row.studentName || "").trim()) || personName(row) || null;
};

const PILLAR_ORDER = ["behaviour", "academic", "technical"] as const;
const PILLAR_LABEL: Record<(typeof PILLAR_ORDER)[number], string> = {
  behaviour: "Behavioural",
  academic: "Academic",
  technical: "Technical",
};

const teacherNameByUserId = async (userId: string): Promise<string | null> => {
  try {
    const user = await Profile(userId);
    const direct = personName(user) || personName(user?.teacherProfileDetails);
    if (direct) return direct;
    const teacherId = user?.role === "teacher" ? user?.id : user?.teacherId;
    if (!teacherId) return null;
    const t = await teacherProfile(teacherId);
    return personName(t?.teacherProfileDetails) || personName(t);
  } catch {
    return null;
  }
};

type Verdict = "valid" | "false" | "unsure";
type Tab = "review" | "scores" | "transcript" | "presence";

const SEV_RANK: Record<Severity, number> = { high: 0, medium: 1, low: 2 };
const SEV_LABEL: Record<Severity, string> = { high: "High", medium: "Medium", low: "Low" };
const SEV_TONE = { high: "danger", medium: "warning", low: "accent" } as const;

const mmss = (s: number | null | undefined) => {
  const t = Math.max(0, Math.round(s || 0));
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const sec = String(t % 60).padStart(2, "0");
  return h ? `${h}:${String(m).padStart(2, "0")}:${sec}` : `${String(m).padStart(2, "0")}:${sec}`;
};
const mins = (s: number | undefined) => `${Math.round((s || 0) / 60)} min`;
const initials = (name: string | null, fallback: string) =>
  (name || fallback)
    .split(/\s+/)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
const roleLabel = (k: string) => k.replace("learner", "student").replace("_", " ");

const TABS: { key: Tab; label: string; icon: React.ReactNode }[] = [
  { key: "review", label: "Review", icon: <PlayCircle size={18} /> },
  { key: "scores", label: "Scores", icon: <Star size={18} /> },
  { key: "transcript", label: "Transcript", icon: <List size={18} /> },
  { key: "presence", label: "Presence", icon: <BarChart2 size={18} /> },
];

/** The review's clock; real recordings (when the class has them) are kept in sync with it. */
const usePlayer = (duration: number) => {
  const [t, setT] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [rate, setRate] = useState(1);
  useEffect(() => {
    if (!playing) return;
    const id = window.setInterval(() => {
      setT((x) => {
        const next = x + 0.25 * rate;
        if (next >= duration) {
          setPlaying(false);
          return duration;
        }
        return next;
      });
    }, 250);
    return () => window.clearInterval(id);
  }, [playing, rate, duration]);
  const seek = (s: number) => setT(Math.max(0, Math.min(duration, s)));
  return { t, playing, rate, setRate, seek, toggle: () => setPlaying((p) => !p) };
};

const VideoCallQaReview: React.FC = () => {
  const { lessonKey } = useParams();
  const navigate = useNavigate();
  const [review, setReview] = useState<QaReview | null>(lessonKey === "sample" ? SAMPLE_REVIEW : null);
  const [loading, setLoading] = useState(lessonKey !== "sample");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!lessonKey || lessonKey === "sample") return;
    let live = true;
    setLoading(true);
    setError(null);
    getVideoCallQaReview(lessonKey)
      .then((r) => live && setReview(r))
      .catch((e) => live && setError(e?.response?.data?.detail || e?.message || "Could not load the review"))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [lessonKey]);

  // Names are not stored in QA (ids only); look them up in the CMS at display time.
  const teacherId = review?.teacher.external_id;
  const studentId = review?.learner.external_id;
  useEffect(() => {
    if (!teacherId && !studentId) return;
    let live = true;
    Promise.all([
      teacherId && !review?.teacher.name ? teacherNameByUserId(teacherId) : Promise.resolve(null),
      studentId && !review?.learner.name
        ? studentNameByUserId(String(studentId))
        : Promise.resolve(null),
    ]).then(([t, students]) => {
      if (!live) return;
      const tName = t;
      const sName = students;
      if (tName || sName)
        setReview((r) =>
          r && {
            ...r,
            teacher: { ...r.teacher, name: r.teacher.name || tName },
            learner: { ...r.learner, name: r.learner.name || sName },
          }
        );
    });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teacherId, studentId]);

  if (loading) {
    return (
      <Page>
        <Card>
          <EmptyState title="Loading the review…" description={`Class ${lessonKey}`} />
        </Card>
      </Page>
    );
  }
  if (error) {
    return (
      <Page>
        <Card>
          <EmptyState
            title="Could not load this review"
            description={String(error)}
            action={
              <Button variant="primary" onClick={() => navigate("/pages/video-call-qa")}>
                Back to Video Call QA
              </Button>
            }
          />
        </Card>
      </Page>
    );
  }
  if (!review) {
    return (
      <Page>
        <Card>
          <EmptyState
            title="Report not available yet"
            description="This class has not been analysed yet, or its report could not be loaded."
          />
        </Card>
      </Page>
    );
  }
  return <ReviewScreen review={review} />;
};

const ReviewScreen: React.FC<{ review: QaReview }> = ({ review }) => {
  const navigate = useNavigate();
  const duration = review.stats.duration_s || review.duration_min * 60;
  const player = usePlayer(duration);
  const [muted, setMuted] = useState(false);
  const [tab, setTab] = useState<Tab>("review");
  const [sort, setSort] = useState<"time" | "severity">("time");
  const [hidden, setHidden] = useState<Set<Severity>>(new Set());
  const [pillarFilter, setPillarFilter] = useState<"all" | (typeof PILLAR_ORDER)[number]>("all");
  const [active, setActive] = useState<string | null>(null);
  const [verdicts, setVerdicts] = useState<Record<string, { v: Verdict; note: string }>>({});
  const playerRef = useRef<HTMLDivElement>(null);
  const videoEls = useRef<Record<string, HTMLVideoElement | null>>({});
  // Transcript follows playback: the current line is kept in view unless the reviewer scrolls.
  const txRef = useRef<HTMLDivElement>(null);
  const txLines = useRef<(HTMLButtonElement | null)[]>([]);
  const [followTx, setFollowTx] = useState(true);
  // Scores tab: each parameter's check cards fold away (collapsed by default).
  const [openChecks, setOpenChecks] = useState<Set<string>>(new Set());
  const toggleChecks = (key: string) =>
    setOpenChecks((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // The real recordings follow the screen's clock: play/pause together, same speed, and a seek
  // (or drift over 1.5 s) moves them to the clock's time.
  useEffect(() => {
    Object.values(videoEls.current).forEach((v) => {
      if (!v) return;
      v.playbackRate = player.rate;
      if (Math.abs(v.currentTime - player.t) > 1.5) v.currentTime = Math.min(player.t, v.duration || player.t);
      if (player.playing && v.paused) v.play().catch(() => undefined);
      if (!player.playing && !v.paused) v.pause();
    });
  }, [player.t, player.playing, player.rate]);

  // Blank speech rows (timelines before f8) say nothing; the chat shows what was said.
  const txRows = useMemo(
    () => review.transcript.filter((r) => r.activity !== "speech" || (r.text || "").trim()),
    [review.transcript],
  );
  const nowIdx = useMemo(() => {
    let idx = -1;
    txRows.forEach((r, i) => {
      if (r.t_start <= player.t) idx = i;
    });
    return idx;
  }, [txRows, player.t]);

  useEffect(() => {
    const box = txRef.current;
    const line = nowIdx >= 0 ? txLines.current[nowIdx] : null;
    if (tab !== "transcript" || !followTx || !box || !line) return;
    const top = line.offsetTop - box.clientHeight / 3; // scroll the box only, never the page
    if (Math.abs(box.scrollTop - top) > 4) box.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
  }, [nowIdx, followTx, tab]);

  // Flags filter by pillar (chips) and severity (legend); severity counts follow the pillar.
  const inPillar = useMemo(
    () => review.flags.filter((f) => pillarFilter === "all" || f.pillar === pillarFilter),
    [review.flags, pillarFilter],
  );
  const pillarCounts = useMemo(() => {
    const c: Record<string, number> = { all: review.flags.length };
    review.flags.forEach((f) => {
      if (f.pillar) c[f.pillar] = (c[f.pillar] || 0) + 1;
    });
    return c;
  }, [review.flags]);
  const counts = useMemo(() => {
    const c: Record<Severity, number> = { high: 0, medium: 0, low: 0 };
    inPillar.forEach((f) => c[f.severity]++);
    return c;
  }, [inPillar]);

  const flags = useMemo(() => {
    const list = inPillar.filter((f) => !hidden.has(f.severity));
    const byTime = (a: QaFlag, b: QaFlag) => (a.t_start ?? 1e9) - (b.t_start ?? 1e9);
    return list.sort(
      sort === "severity"
        ? (a, b) => SEV_RANK[a.severity] - SEV_RANK[b.severity] || byTime(a, b)
        : (a, b) => byTime(a, b) || SEV_RANK[a.severity] - SEV_RANK[b.severity],
    );
  }, [inPillar, hidden, sort]);

  const activeId = active ?? flags[0]?.id ?? null;

  const focus = (f: QaFlag) => {
    setActive(f.id);
    if (f.t_start !== null) player.seek(Math.max(0, f.t_start - 2));
  };

  const setVerdict = (id: string, v: Verdict) =>
    setVerdicts((all) => ({ ...all, [id]: { v, note: all[id]?.note || "" } }));
  const setNote = (id: string, note: string) =>
    setVerdicts((all) => ({ ...all, [id]: { v: all[id]?.v, note } as { v: Verdict; note: string } }));

  // Keyboard: J/K next/previous flag, 1/2/3 verdict, space play/pause.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (el && el.closest && el.closest("input, textarea, select")) return;
      const i = flags.findIndex((f) => f.id === activeId);
      if (e.key === " ") {
        e.preventDefault();
        player.toggle();
      } else if (e.key === "j" && flags.length) focus(flags[Math.min(flags.length - 1, i + 1)]);
      else if (e.key === "k" && flags.length) focus(flags[Math.max(0, i - 1)]);
      else if (["1", "2", "3"].includes(e.key) && activeId) {
        setVerdict(activeId, (["valid", "false", "unsure"] as Verdict[])[Number(e.key) - 1]);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const pct = (s: number) => `${Math.min(100, (100 * s) / duration)}%`;
  const ticks = useMemo(() => {
    const step = duration > 3600 ? 600 : duration > 1200 ? 300 : 60;
    const out: number[] = [];
    for (let x = 0; x < duration - step / 3; x += step) out.push(x);
    out.push(duration);
    return out;
  }, [duration]);

  const score = review.qa_score;
  const scoreTone = score === null ? "" : score >= 75 ? "good" : score >= 50 ? "mid" : "bad";
  const tiles = (["teacher", "learner"] as const).map((role) => ({
    role,
    name: role === "learner" ? review.learner.name || "Student" : review.teacher.name || "Teacher",
  }));
  // Screen share: presence keys like "teacher_screen" carry when it was on (recording clock).
  // The recorded cameras go black while sharing, so the shared screen becomes the main tile.
  const shareSpans = Object.entries(review.presence?.roles || {})
    .filter(([key, p]) => key.includes("screen") && p.first_join_t !== null && p.last_leave_t !== null)
    .map(([, p]) => [p.first_join_t as number, p.last_leave_t as number] as const);
  const screenUrl = review.videos?.screen;
  const sharing = !!screenUrl && shareSpans.some(([a, b]) => player.t >= a && player.t <= b);
  const screenShare = review.scores.find((s) => s.parameter_key === "screen_share") || null;
  const lesson = review.scores.find((s) => s.parameter_key === "lesson_alignment") || null;
  const lessonOf = (prefix: string) => (lesson?.checks || []).filter((c) => c.key.startsWith(prefix));
  const st = review.stats;
  const share = (x?: number) => (x === undefined ? "" : ` (${Math.round((100 * x) / duration)}%)`);

  return (
    <Page className="vcqa">
      <button type="button" className="vcqa-back" onClick={() => navigate("/pages/video-call-qa")}>
        <ArrowLeft size={16} /> All classes
      </button>

      {review.class_id === "sample" && (
        <Callout tone="info">
          Sample report with dummy data, to walk through the review screen. Verdicts here are not saved.
        </Callout>
      )}

      {/* ---------- header ---------- */}
      <section className="ds-card vcqa-head">
        <div className="vcqa-person">
          <div className="vcqa-face vcqa-face--learner">{initials(review.learner.name, "S")}</div>
          <div>
            <div className="vcqa-name">{review.learner.name || "Student"}</div>
            <div className="vcqa-muted">
              {[
                review.learner.external_id && `Student user ID ${review.learner.external_id}`,
                review.learner_age_band && `Age ${review.learner_age_band}`,
                review.instrument,
              ]
                .filter(Boolean)
                .join("  |  ")}
            </div>
          </div>
        </div>
        <span className="vcqa-muted">with</span>
        <div className="vcqa-person">
          <div className="vcqa-face vcqa-face--teacher">{initials(review.teacher.name, "T")}</div>
          <div>
            <div className="vcqa-name">{review.teacher.name || "Teacher"}</div>
            <div className="vcqa-muted">Teacher user ID {review.teacher.external_id || "–"}</div>
          </div>
        </div>
        <div className="vcqa-sep" />
        <div className="vcqa-person">
          <Calendar size={26} className="vcqa-muted" />
          <div>
            <div className="vcqa-name">{dayjs(review.scheduled_start).format("D MMM YYYY, h:mm A")}</div>
            <div className="vcqa-muted">
              Class ID {review.class_schedule_id}  |  {Math.round(review.duration_min)} min{review.scheduled_duration_min ? ` (scheduled ${review.scheduled_duration_min} min)` : ""}  |  {review.class_type}
            </div>
          </div>
        </div>
        {review.pillar_scores ? (
          <div className="vcqa-pillars">
            {PILLAR_ORDER.map((p) => {
              const v = review.pillar_scores?.[p] ?? null;
              const tone = v === null ? "" : v >= 75 ? "good" : v >= 50 ? "mid" : "bad";
              return (
                <div key={p} className={`vcqa-score vcqa-score--${tone}`} title={`${PILLAR_LABEL[p]}: mean of its parameter scores, 0–100`}>
                  <div className="vcqa-muted vcqa-small">{PILLAR_LABEL[p]}</div>
                  <div className="vcqa-score__value">
                    {v ?? "–"} <span>/ 100</span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className={`vcqa-score vcqa-score--${scoreTone}`} title="Mean of the parameter scores, each scaled to 0–100">
            <div className="vcqa-muted vcqa-small">QA Score</div>
            <div className="vcqa-score__value">
              {score ?? "–"} <span>/ 100</span> <Info size={14} />
            </div>
          </div>
        )}
        <div>
          <div className="vcqa-name">
            Auto Flag <span className="vcqa-count">{review.flags.length}</span>
          </div>
          <div className="vcqa-muted vcqa-small">
            ({counts.high} High, {counts.medium} Medium, {counts.low} Low)
          </div>
        </div>
      </section>

      <div className="vcqa-grid">

        {/* ---------- centre ---------- */}
        <div className="vcqa-main">
          <div className="vcqa-player" ref={playerRef}>
            <div className={`vcqa-stage${sharing ? " is-sharing" : ""}`}>
              {screenUrl && (
                <div className="vcqa-tile vcqa-tile--screen" aria-hidden={!sharing}>
                  <video
                    ref={(el) => {
                      videoEls.current.screen = el;
                    }}
                    className="vcqa-tile__video"
                    src={screenUrl}
                    muted
                    playsInline
                    preload="metadata"
                    style={{ width: "100%", height: "100%", objectFit: "contain", background: "#000" }}
                  />
                  <span className="vcqa-tile__who">Screen share</span>
                </div>
              )}
              {tiles.map((tile) => (
                <div key={tile.role} className="vcqa-tile">
                  {review.videos?.[tile.role] ? (
                    <video
                      ref={(el) => {
                        videoEls.current[tile.role] = el;
                      }}
                      className="vcqa-tile__video"
                      src={review.videos[tile.role]}
                      muted={muted}
                      playsInline
                      preload="metadata"
                      style={{ width: "100%", height: "100%", objectFit: "cover", background: "#000" }}
                    />
                  ) : (
                    <div className="vcqa-tile__ph">
                      <b>{initials(tile.name, "?")}</b>
                      {review.class_id === "sample" ? "Recording preview" : "No video"}
                    </div>
                  )}
                  <span className="vcqa-tile__who">{tile.name}</span>
                </div>
              ))}
            </div>
            <div className="vcqa-controls">
              <div
                className="vcqa-seek"
                onClick={(e) => {
                  const r = e.currentTarget.getBoundingClientRect();
                  player.seek((duration * (e.clientX - r.left)) / r.width);
                }}
              >
                <div className="vcqa-seek__rail" />
                {shareSpans.map(([a, b]) => (
                  <i
                    key={`share-${a}`}
                    className="vcqa-seek__share"
                    title="Screen share"
                    style={{ left: pct(a), width: `calc(${pct(b)} - ${pct(a)})` }}
                  />
                ))}
                <div className="vcqa-seek__fill" style={{ width: pct(player.t) }} />
                {review.flags.map((f) =>
                  f.t_start === null ? null : (
                    <i key={f.id} className={`vcqa-seek__mark is-${f.severity}`} style={{ left: pct(f.t_start) }} />
                  ),
                )}
              </div>
              <div className="vcqa-bar">
                <button type="button" onClick={player.toggle} title="Play / pause (space)">
                  {player.playing ? <Pause size={20} /> : <Play size={20} />}
                </button>
                <button type="button" onClick={() => setMuted(!muted)} title="Mute">
                  {muted ? <VolumeX size={20} /> : <Volume2 size={20} />}
                </button>
                <span className="vcqa-time">
                  {mmss(player.t)} / {mmss(duration)}
                </span>
                <select
                  className="vcqa-rate"
                  value={player.rate}
                  onChange={(e) => player.setRate(Number(e.target.value))}
                >
                  {[0.75, 1, 1.25, 1.5, 2].map((r) => (
                    <option key={r} value={r}>
                      {r}x
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  title="Full screen"
                  onClick={() =>
                    document.fullscreenElement ? document.exitFullscreen() : playerRef.current?.requestFullscreen()
                  }
                >
                  <Maximize size={18} />
                </button>
              </div>
            </div>
          </div>

          {/* ---------- tabs ---------- */}
          <nav className="ds-card vcqa-nav" role="tablist">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={tab === t.key}
                className={tab === t.key ? "is-on" : ""}
                onClick={() => setTab(t.key)}
              >
                {t.icon}
                {t.label}
              </button>
            ))}
          </nav>

          {tab === "review" && (
            <Card
              title="AI Flag Timeline"
              actions={
                <div className="vcqa-flag-filters">
                <div className="vcqa-pillar-chips" role="group" aria-label="Filter flags by area">
                  {(["all", ...PILLAR_ORDER] as const).map((p) => (
                    <button
                      key={p}
                      type="button"
                      className={pillarFilter === p ? "is-on" : ""}
                      onClick={() => setPillarFilter(p)}
                    >
                      {p === "all" ? "All" : PILLAR_LABEL[p]} ({pillarCounts[p] || 0})
                    </button>
                  ))}
                </div>
                <div className="vcqa-legend">
                  {(["high", "medium", "low"] as Severity[]).map((s) => (
                    <button
                      key={s}
                      type="button"
                      className={hidden.has(s) ? "is-off" : ""}
                      onClick={() =>
                        setHidden((h) => {
                          const n = new Set(h);
                          n.has(s) ? n.delete(s) : n.add(s);
                          return n;
                        })
                      }
                    >
                      <span className={`vcqa-dot is-${s}`} />
                      {SEV_LABEL[s]} ({counts[s]})
                    </button>
                  ))}
                </div>
                </div>
              }
            >
              <div className="vcqa-track">
                <div className="vcqa-track__base" />
                {flags.map((f) =>
                  f.t_start === null || f.t_end === null ? null : (
                    <div
                      key={`s-${f.id}`}
                      className={`vcqa-track__span is-${f.severity}`}
                      style={{ left: pct(f.t_start), width: `${Math.max(0.4, (100 * (f.t_end - f.t_start)) / duration)}%` }}
                    />
                  ),
                )}
                {flags.map((f) =>
                  f.t_start === null ? null : (
                    <button
                      key={f.id}
                      type="button"
                      title={`${mmss(f.t_start)} · ${f.title}`}
                      className={`vcqa-track__pin is-${f.severity} ${f.id === activeId ? "is-on" : ""}`}
                      style={{ left: pct(f.t_start) }}
                      onClick={() => focus(f)}
                    />
                  ),
                )}
                <div className="vcqa-track__head" style={{ left: pct(player.t) }} />
              </div>
              <div className="vcqa-ticks">
                {ticks.map((x, i) => (
                  <span
                    key={x}
                    style={{ left: pct(x) }}
                    className={i === 0 ? "is-first" : i === ticks.length - 1 ? "is-last" : ""}
                  >
                    {mmss(x)}
                  </span>
                ))}
              </div>

              <h3 className="vcqa-h3">
                Flagged Moments <span className="vcqa-muted">(click to seek)</span>
              </h3>
              <div className="vcqa-moments">
                {flags.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    className={`vcqa-moment ${f.id === activeId ? `is-on is-${f.severity}` : ""}`}
                    onClick={() => focus(f)}
                  >
                    <div className="vcqa-thumb">
                      <span className="vcqa-thumb__ini">{f.side === "teacher" ? "T" : "S"}</span>
                      <span className="vcqa-thumb__t">{mmss(f.t_start)}</span>
                    </div>
                    <div className="vcqa-moment__title">
                      <span className={`vcqa-dot is-${f.severity}`} />
                      {f.title}
                    </div>
                    <div className="vcqa-moment__sev">{SEV_LABEL[f.severity]}</div>
                  </button>
                ))}
                {!flags.length && <span className="vcqa-muted">No flags at the selected severities.</span>}
              </div>
            </Card>
          )}

          {tab === "review" && lesson && (
            <Card title="Lesson: marked vs taught" subtitle={lesson.summary || undefined}>
              <div className="vcqa-lesson">
                <div>
                  <div className="vcqa-rubrics__subhead">Taught in class</div>
                  {lessonOf("taught@").length ? (
                    lessonOf("taught@").map((c) => (
                      <button
                        key={c.key}
                        type="button"
                        className="vcqa-lesson__topic"
                        onClick={() => c.t_s !== null && player.seek(c.t_s)}
                        title="Play from here"
                      >
                        <b>{c.label}</b>
                        <span className="vcqa-small vcqa-muted">
                          {c.t_s !== null ? mmss(c.t_s) : ""} {c.note ? `· ${c.note}` : ""}
                        </span>
                      </button>
                    ))
                  ) : (
                    <span className="vcqa-muted">Nothing identified from the transcript.</span>
                  )}
                </div>
                <div>
                  <div className="vcqa-rubrics__subhead">Marked in LMS</div>
                  {lessonOf("marked:").length ? (
                    lessonOf("marked:").map((c) => (
                      <div key={c.key} className={`vcqa-lesson__marked is-${c.answer || "unknown"}`}>
                        <b>
                          {c.answer === "covered" ? "✓" : c.answer === "partly" ? "◐" : "✗"}{" "}
                          {c.label.replace(/^Marked: /, "")}
                        </b>
                        {c.note && <span className="vcqa-small vcqa-muted">{c.note}</span>}
                      </div>
                    ))
                  ) : (
                    <span className="vcqa-muted">No topic titles received from the LMS for this class.</span>
                  )}
                  {lessonOf("unmarked@").length > 0 && (
                    <>
                      <div className="vcqa-rubrics__subhead" style={{ marginTop: 12 }}>Taught, not marked</div>
                      {lessonOf("unmarked@").map((c) => (
                        <div key={c.key} className="vcqa-small">• {c.label.replace(/^Taught, not marked: /, "")}</div>
                      ))}
                    </>
                  )}
                </div>
              </div>
            </Card>
          )}

          {tab === "review" && screenShare && (
            <Card title="What was shared" subtitle={screenShare.summary || undefined}>
              {screenShare.checks && screenShare.checks.length > 0 ? (
                <div className="vcqa-shared">
                  {screenShare.checks.map((c) => (
                    <button
                      key={c.key}
                      type="button"
                      className="vcqa-shared__item"
                      onClick={() => c.t_s !== null && player.seek(c.t_s)}
                      title="Play from here"
                    >
                      {c.frame_url ? <img src={c.frame_url} alt="" loading="lazy" /> : <div className="vcqa-check__noimg" />}
                      <b>{c.label}</b>
                      <span className="vcqa-small vcqa-muted">{c.note}</span>
                    </button>
                  ))}
                </div>
              ) : (
                <span className="vcqa-muted">{screenShare.summary || "No screen share in this class."}</span>
              )}
            </Card>
          )}

          {tab === "scores" && (
            <Card
              title="Parameter scores"
              actions={
                review.scores.some((s) => s.checks?.length) && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      setOpenChecks(
                        openChecks.size
                          ? new Set()
                          : new Set(review.scores.filter((s) => s.checks?.length).map((s) => s.parameter_key)),
                      )
                    }
                  >
                    {openChecks.size ? "Collapse all" : "Expand all"}
                  </Button>
                )
              }
            >
              <table className="vcqa-table">
                <thead>
                  <tr>
                    <th>Parameter</th>
                    <th>Score</th>
                    <th />
                    <th>Confidence</th>
                    <th>Summary</th>
                  </tr>
                </thead>
                <tbody>
                  {[...review.scores]
                    .sort((a, b) => PILLAR_ORDER.indexOf((a.pillar || "technical") as never) - PILLAR_ORDER.indexOf((b.pillar || "technical") as never))
                    .map((s, i, all) => (
                    <React.Fragment key={s.parameter_key}>
                    {(i === 0 || all[i - 1].pillar !== s.pillar) && (
                      <tr className="vcqa-pillar-row">
                        <td colSpan={5}>
                          <b>{s.pillar ? PILLAR_LABEL[s.pillar] : "Other"}</b>
                          {s.pillar && review.pillar_scores?.[s.pillar] !== undefined && (
                            <span className="vcqa-muted"> · {review.pillar_scores?.[s.pillar] ?? "–"} / 100</span>
                          )}
                        </td>
                      </tr>
                    )}
                    <tr
                      className={s.checks?.length ? "vcqa-param is-foldable" : "vcqa-param"}
                      onClick={() => s.checks?.length && toggleChecks(s.parameter_key)}
                    >
                      <td>
                        <b>
                          {s.checks?.length ? (
                            <span className={`vcqa-fold${openChecks.has(s.parameter_key) ? " is-open" : ""}`} aria-hidden>
                              ▸
                            </span>
                          ) : null}
                          {s.title}
                        </b>
                        <div className="vcqa-muted vcqa-small">
                          {s.parameter_key}
                          {s.checks?.length ? ` · ${s.checks.length} ${s.checks.length === 1 ? "item" : "items"}` : ""}
                        </div>
                      </td>
                      <td>{s.score === null ? "–" : s.scale === "1-5" ? `${s.score} / 5` : `${Math.round(s.score)}%`}</td>
                      <td>
                        <div className="vcqa-meter">
                          <i
                            className={(s.normalized ?? 0) < 40 ? "is-high" : (s.normalized ?? 0) < 60 ? "is-medium" : "is-ok"}
                            style={{ width: `${s.normalized ?? 0}%` }}
                          />
                        </div>
                      </td>
                      <td>{s.confidence === null ? "–" : `${Math.round(100 * s.confidence)}%`}</td>
                      <td className="vcqa-muted">
                        {s.summary}
                        {s.missing_elements.length > 0 && (
                          <div className="vcqa-small">Missing: {s.missing_elements.join(", ")}</div>
                        )}
                      </td>
                    </tr>
                    {s.checks && s.checks.length > 0 && openChecks.has(s.parameter_key) && (
                      <tr className="vcqa-checks-row">
                        <td colSpan={5}>
                          <div className="vcqa-checks">
                            {s.checks.map((c) => (
                              <div
                                key={c.key}
                                className={`vcqa-check ${
                                  c.severity === "high"
                                    ? "is-high"
                                    : c.severity === "medium"
                                      ? "is-medium"
                                      : c.severity === "low"
                                        ? "is-low"
                                        : c.met === true
                                          ? "is-ok"
                                          : c.met === false
                                            ? "is-high"
                                            : "is-unknown"
                                }`}
                              >
                                {c.frame_url && (
                                  <a href={c.frame_url} target="_blank" rel="noreferrer" title="Open frame">
                                    <img src={c.frame_url} alt="" loading="lazy" />
                                  </a>
                                )}
                                <div>
                                  <div className="vcqa-check__label">
                                    {c.answer === "info" ? "🖥" : c.answer === "minor" ? "~" : c.met === true ? "✓" : c.met === false ? "✗" : "?"}{" "}
                                    {c.label}
                                    {c.severity && c.answer !== "yes" && (
                                      <span className={`vcqa-sev is-${c.severity}`}>{c.answer === "minor" ? "minor" : c.severity}</span>
                                    )}
                                  </div>
                                  {c.note && <div className="vcqa-small vcqa-muted">{c.note}</div>}
                                  {!!c.t_s && (
                                    <button type="button" className="vcqa-linkbtn vcqa-small" onClick={() => player.seek(c.t_s ?? 0)}>
                                      at {mmss(c.t_s)}
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </Card>
          )}

          {tab === "transcript" && (
            <Card
              title="Transcript"
              subtitle="Source language, untranslated. Follows playback; click a line to seek."
              actions={
                !followTx && (
                  <Button size="sm" variant="secondary" onClick={() => setFollowTx(true)}>
                    Follow playback
                  </Button>
                )
              }
            >
              <div
                className="vcqa-tx vcqa-chat"
                ref={txRef}
                onWheel={() => setFollowTx(false)}
                onTouchMove={() => setFollowTx(false)}
              >
                {txRows.map((r, i) => {
                  const now = i === nowIdx;
                  const side = r.speaker === "teacher" ? "left" : "right";
                  const sameAsPrev = i > 0 && txRows[i - 1].speaker === r.speaker;
                  return (
                    <div key={i} className={`vcqa-chat__row is-${side}${sameAsPrev ? " is-cont" : ""}`}>
                      <button
                        ref={(el) => {
                          txLines.current[i] = el;
                        }}
                        type="button"
                        className={`vcqa-chat__bubble is-${r.speaker}${now ? " is-now" : ""}${r.activity !== "speech" ? " is-music" : ""}`}
                        onClick={() => {
                          player.seek(r.t_start);
                          setFollowTx(true);
                        }}
                      >
                        {!sameAsPrev && <span className={`vcqa-chat__who is-${r.speaker}`}>{roleLabel(r.speaker)}</span>}
                        <span className="vcqa-chat__text">
                          {r.activity !== "speech" && (
                            <i className="vcqa-chat__tag">{r.activity === "demo" ? "♪ demo" : r.activity === "attempt" ? "♪ attempt" : r.activity}</i>
                          )}
                          {r.text}
                        </span>
                        <span className="vcqa-chat__time">{mmss(r.t_start)}</span>
                      </button>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          {tab === "presence" && review.call_quality && Object.keys(review.call_quality).length > 0 && (
            <Card
              title="Call quality by direction"
              subtitle={`What each person received of the other's audio and video${review.quality_source === "client" ? " (from the class app; Agora had no data)" : " (from Agora Analytics)"}. Causes are on the incident cards.`}
            >
              <table className="vcqa-table">
                <thead>
                  <tr>
                    <th>Direction</th>
                    <th>Audio lost</th>
                    <th>Video lost</th>
                    <th>Audio frozen</th>
                    <th>Video frozen</th>
                    <th>Frames (sent → received)</th>
                    <th>Resolution</th>
                  </tr>
                </thead>
                <tbody>
                  {(["teacher_to_learner", "learner_to_teacher"] as const)
                    .filter((k) => review.call_quality?.[k])
                    .map((k) => {
                      const d = review.call_quality![k];
                      const pctTxt = (v: number | null) => (v === null || v === undefined ? "–" : `${v.toFixed(1)}%`);
                      const bad = (v: number | null, lim: number) => (v !== null && v !== undefined && v >= lim ? "vcqa-bad" : "");
                      return (
                        <tr key={k}>
                          <td><b>{k === "teacher_to_learner" ? "Teacher → Student" : "Student → Teacher"}</b></td>
                          <td className={bad(d.audio_loss_pct, 3)}>{pctTxt(d.audio_loss_pct)}</td>
                          <td className={bad(d.video_loss_pct, 3)}>{pctTxt(d.video_loss_pct)}</td>
                          <td className={bad(d.audio_frozen_s, 10)}>{mmss(d.audio_frozen_s)}</td>
                          <td className={bad(d.video_frozen_s, 30)}>{mmss(d.video_frozen_s)}</td>
                          <td className={d.frames_received_pct !== null && d.frames_received_pct < 85 ? "vcqa-bad" : ""}>
                            {d.sent_fps ?? "–"} → {d.received_fps ?? "–"} fps
                            {d.frames_received_pct !== null ? ` (${Math.round(d.frames_received_pct)}%)` : ""}
                          </td>
                          <td>{d.resolution || "–"}</td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </Card>
          )}

          {tab === "presence" && (
            <Card title="Who was in the call" subtitle="From Agora Analytics: time in the channel, rejoins and freeze rates.">
              {review.presence?.no_show && (
                <Callout tone="danger">
                  {review.presence.no_show === "teacher" ? "Teacher" : "Student"} was in the call for under{" "}
                  {Math.round(review.presence.no_show_threshold_s / 60)} min while the other stayed.
                </Callout>
              )}
              {review.presence ? (
                <table className="vcqa-table">
                  <thead>
                    <tr>
                      <th>Role</th>
                      <th>Present</th>
                      <th>Sessions</th>
                      <th>Rejoins</th>
                      <th>Audio freeze</th>
                      <th>Video freeze</th>
                      <th>Platform</th>
                      <th style={{ width: "30%" }}>In call</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(review.presence.roles).map(([k, r]) => (
                      <tr key={k}>
                        <td>
                          <b className="vcqa-cap">{roleLabel(k)}</b>
                        </td>
                        <td>{mmss(r.present_s)}</td>
                        <td>{r.sessions}</td>
                        <td>{r.rejoins}</td>
                        <td>{r.audio_freeze_rate ?? "–"}</td>
                        <td>{r.video_freeze_rate ?? "–"}</td>
                        <td>{r.platforms.join(", ")}</td>
                        <td>
                          <div className="vcqa-lane">
                            <i style={{ left: pct(r.first_join_t || 0), width: pct(r.present_s) }} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <EmptyState title="No presence data" description="Agora Analytics was unavailable for this class." />
              )}
            </Card>
          )}
        </div>

        {/* ---------- flags panel ---------- */}
        <aside className="ds-card vcqa-flags">
          <div className="vcqa-flags__head">
            <h2>
              Flags ({flags.length === review.flags.length ? review.flags.length : `${flags.length} of ${review.flags.length}`})
            </h2>
            <Select
              value={sort}
              onChange={(e) => setSort(e.target.value as "time" | "severity")}
              options={[
                { value: "time", label: "Sort by time" },
                { value: "severity", label: "Sort by severity" },
              ]}
            />
          </div>
          <div className="vcqa-flags__list">
            {flags.map((f) => {
              const on = f.id === activeId;
              const vd = verdicts[f.id];
              return (
                <div
                  key={f.id}
                  className={`vcqa-flag ${on ? `is-on is-${f.severity}` : ""}`}
                  onClick={() => !on && focus(f)}
                >
                  <div className="vcqa-thumb vcqa-thumb--sm">
                    <span className="vcqa-thumb__ini">{f.side === "teacher" ? "T" : "S"}</span>
                    <span className="vcqa-thumb__play">
                      <Play size={10} />
                    </span>
                  </div>
                  <div>
                    <div className="vcqa-flag__when">
                      <Clock size={13} />
                      {f.t_start === null ? "Whole class" : `${mmss(f.t_start)} - ${mmss(f.t_end)}`}
                      <Badge tone={SEV_TONE[f.severity]}>{SEV_LABEL[f.severity]}</Badge>
                    </div>
                    <div className="vcqa-flag__title">{f.title}</div>
                    {f.detail && <div className="vcqa-muted vcqa-small">{f.detail}</div>}
                    {!on && vd?.v && (
                      <div className="vcqa-small vcqa-muted">
                        Marked <b>{vd.v === "valid" ? "Valid" : vd.v === "false" ? "False flag" : "Not sure"}</b>
                      </div>
                    )}
                  </div>
                  {on && (
                    <div className="vcqa-verdict" onClick={(e) => e.stopPropagation()}>
                      <div className="vcqa-verdict__q">Is this a valid flag?</div>
                      <div className="vcqa-verdict__btns">
                        <Button
                          size="sm"
                          variant={vd?.v === "valid" ? "danger" : "secondary"}
                          onClick={() => setVerdict(f.id, "valid")}
                        >
                          Valid
                        </Button>
                        <Button
                          size="sm"
                          variant={vd?.v === "false" ? "primary" : "secondary"}
                          onClick={() => setVerdict(f.id, "false")}
                        >
                          False Flag
                        </Button>
                        <Button
                          size="sm"
                          variant={vd?.v === "unsure" ? "accent" : "secondary"}
                          onClick={() => setVerdict(f.id, "unsure")}
                        >
                          Not Sure
                        </Button>
                      </div>
                      <TextArea
                        rows={2}
                        placeholder="Add a note (optional)…"
                        value={vd?.note || ""}
                        onChange={(e) => setNote(f.id, e.target.value)}
                      />
                      {vd?.v && <div className="vcqa-small vcqa-saved">Saved locally (sample)</div>}
                    </div>
                  )}
                </div>
              );
            })}
            {!flags.length && <EmptyState title="No flags" description="Nothing at the selected severities." />}
          </div>
          <div className="vcqa-keys vcqa-small vcqa-muted">
            <kbd>J</kbd>/<kbd>K</kbd> next / previous · <kbd>1</kbd> valid · <kbd>2</kbd> false flag · <kbd>3</kbd>{" "}
            not sure · <kbd>space</kbd> play
          </div>
        </aside>
      </div>

      {/* ---------- summary strip ---------- */}
      <section className="ds-card vcqa-summary">
        <div className="vcqa-summary__icon">
          <FileText size={22} />
        </div>
        <div>
          <h2 className="vcqa-h3">AI Overall Summary</h2>
          <div className="vcqa-muted">{review.summary || "No summary yet."}</div>
        </div>
        {[
          ["Class Duration", mins(duration)],
          ["Student Talk Time", st.learner_speech_s === undefined ? "–" : mins(st.learner_speech_s) + share(st.learner_speech_s)],
          ["Teacher Talk Time", st.teacher_speech_s === undefined ? "–" : mins(st.teacher_speech_s) + share(st.teacher_speech_s)],
          ["Silence", st.silence_s === undefined ? "–" : mins(st.silence_s) + share(st.silence_s)],
        ].map(([k, v]) => (
          <div key={k} className="vcqa-stat">
            <div className="vcqa-muted vcqa-small">{k}</div>
            <div className="vcqa-stat__v">{v}</div>
          </div>
        ))}
      </section>
    </Page>
  );
};

export default VideoCallQaReview;
